import _get from 'lodash/get'
import * as z from 'zod'

import type { StepConfigParams } from '@flows/configs/types'
import type { FlowValues } from '@flows/core/types/stepTypes'

type GetParamPath = StepConfigParams['helperFunctions']['getParamPath']

export const eligibilitySchemaShape = (getParamPath: GetParamPath) => ({
  [getParamPath('health_insurance_type')]: z.enum([
    'statutory',
    'free_healthcare_for_civil_servants',
    'private',
  ]),
  [getParamPath('number_of_missing_teeth')]: z.enum(['0', '1', '2', '3', '4']),
  [getParamPath('treated_peridontal_disease_3_years')]: z.boolean(),
})

export const getIneligibleStep = (flowValues: FlowValues, getParamPath: GetParamPath) => {
  if (
    _get(flowValues, getParamPath('number_of_missing_teeth')) === '4' ||
    _get(flowValues, getParamPath('treated_peridontal_disease_3_years')) === true
  ) {
    return 'failure'
  }
  if (_get(flowValues, getParamPath('health_insurance_type')) === 'private') {
    return 'dental_already_covered'
  }
  return undefined
}

export const periodontalDiseaseChoice = (getParamPath: GetParamPath) =>
  ({
    type: 'yesNo',
    variant: 'filled',
    shouldTrackValues: true,
    name: getParamPath('treated_peridontal_disease_3_years'),
  }) as const

export const generalNotice = {
  type: 'infoBox',
  bgColor: 'grey200',
  contentConfiguration: [
    {
      type: 'title',
      size: 'h4',
      text: {
        type: 'translatableText',
        translationKey: 'sharedFlows_dental:steps.general_notice.title',
      },
    },
    {
      type: 'text',
      emphasis: 'default',
      style: 'caption',
      text: {
        type: 'translatableText',
        translationKey: 'sharedFlows_dental:steps.general_notice.description',
      },
    },
  ],
} as const

export const healthInsuranceTypeChoice = (getParamPath: GetParamPath) =>
  ({
    type: 'choice',
    variant: 'filled',
    shouldTrackValues: true,
    name: getParamPath('health_insurance_type'),
    options: [
      {
        label: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_dental:steps.dental_instant.health_insurance.options.statutory',
        },
        value: 'statutory',
      },
      {
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_instant.health_insurance.options.free',
        },
        value: 'free_healthcare_for_civil_servants',
      },
      {
        label: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_dental:steps.dental_instant.health_insurance.options.private',
        },
        value: 'private',
      },
    ],
  }) as const

export const infoTooltip = (textKey: string) =>
  ({
    tooltipConfiguration: [
      {
        type: 'text',
        style: 'body2',
        weight: 'bold',
        emphasis: 'default',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_dental:steps.dental_instant.info.title',
        },
      },
      {
        type: 'text',
        text: { type: 'translatableText', translationKey: textKey },
      },
    ],
  }) as const

export const missingTeethCountOptions = [
  { label: { type: 'nonTranslatableText', text: '0' }, value: '0' },
  { label: { type: 'nonTranslatableText', text: '1' }, value: '1' },
  { label: { type: 'nonTranslatableText', text: '2' }, value: '2' },
  { label: { type: 'nonTranslatableText', text: '3' }, value: '3' },
] as const

export const missingTeethChoice = (getParamPath: GetParamPath) =>
  ({
    type: 'choice',
    variant: 'filled',
    shouldTrackValues: true,
    shouldAutoSubmit: false,
    layout: 'horizontal',
    size: 'compact',
    name: getParamPath('number_of_missing_teeth'),
    options: [
      ...missingTeethCountOptions,
      { label: { type: 'nonTranslatableText', text: '4+' }, value: '4' },
    ],
  }) as const

export const previousInsuranceOptions = [
  {
    label: {
      type: 'translatableText',
      translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.options.yes',
    },
    value: 'true',
  },
  {
    label: {
      type: 'translatableText',
      translationKey: 'sharedFlows_dental:steps.dental_previous_insurance.options.no',
    },
    value: 'false',
  },
] as const
